import { HttpErrorResponse, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { AccountBalanceService } from '@frontend/sports/account-balance/feature';
import { BetslipConfig } from '@frontend/sports/common/client-config-data-access';
import { LoggerFactory, SportsRemoteLogger } from '@frontend/sports/common/core/feature/logging';
import { PlaceBetResponse, PlaceBetStatus } from '@frontend/sports/types/betslip';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Action, Store } from '@ngrx/store';
import { EventBridgeService } from 'packages/sports/web/app/src/event-bridge/event-bridge.service';
import { Observable, catchError, concatMap, delay, exhaustMap, from, map, of, startWith, switchMap } from 'rxjs';

import BetslipActions from '../../../base/store/actions';
import { betslipSelector } from '../../../base/store/selectors';
import { BetslipState } from '../../../core/betslip-state';
import { BetslipPlaceBetActions, PlaceBetFailedReason, QueryPlaceStatusPayload } from '../../betplacement/actions';
import { IPlacedBetslipResult } from '../../betplacement/models';
import BetBuilderPlaceBetService from '../../betplacement/services/bet-builder-place-bet-service';
import PlaceBetResponseProcessorService from '../../betplacement/services/place-bet-response-processor.service';
import PlaceBetService from '../../betplacement/services/place-bet.service';
import PlaceBetRequestBuilder from '../../betplacement/services/request/place-bet-request-builder';
import { isPartialSlip, isSucceededNonPartialSlip } from '../../betplacement/utils';
import { OverAskService } from '../../over-ask/services/over-ask.service';
import { RewardTokensActions } from '../../reward-tokens/actions';
import BetslipTrackingActions from '../../tracking/state/tracking.actions';
import { mapSlipToTrackingSlip } from '../../tracking/tracking.utils';
import BetslipErrorsActions from '../../validation/actions';
import { BetslipError } from '../../validation/errors/betslip-error';
import { TechnicalError } from '../../validation/errors/general/technical-error';
import { NotLoggedInError } from '../../validation/errors/user/not-logged-in-error';
import { emptyPickErrors } from '../../validation/state';
import { QuickBetActions } from '../quick-bet-actions';
import { selectLinearQuickBetBuilderState } from '../quick-bet.selectors';
import { QuickBetActionButtonState } from '../quick-bet.state';

@Injectable()
export default class PlaceBetEffects {
    private readonly logger: SportsRemoteLogger;

    constructor(
        private actions$: Actions,
        private store: Store,
        private placeBetService: PlaceBetService,
        private requestBuilder: PlaceBetRequestBuilder,
        private responseProcessor: PlaceBetResponseProcessorService,
        private overAskService: OverAskService,
        private eventBridgeService: EventBridgeService,
        private betBuilderPlaceBetService: BetBuilderPlaceBetService,
        private balanceService: AccountBalanceService,
        private betslipConfig: BetslipConfig,
        loggerFactory: LoggerFactory,
    ) {
        this.logger = loggerFactory.getLogger('PlaceBetEffects');
    }

    handlePlaceBetEffect$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(QuickBetActions.placeBet),
            concatLatestFrom(() => [this.store.select(betslipSelector), this.store.select(selectLinearQuickBetBuilderState)]),
            exhaustMap(([action, state, linearQuickBetBuilderState]) => {
                const { request, requestPicks, trackBetContext, trackBetContextV2, slips } = linearQuickBetBuilderState.isLinearQuickBetBuilder
                    ? this.requestBuilder.buildLinearQuickBetBuilder(state, action.payload.requestId, linearQuickBetBuilderState.pickId)
                    : this.requestBuilder.build(state, action.payload.requestId);

                const balanceBeforePlacement = this.balanceService.currentBalanceProperties?.accountBalance ?? 0;

                this.overAskService.initModern(state, requestPicks, request.betSlips[0]);

                return this.placeBetService.placeBet(request, trackBetContext, trackBetContextV2).pipe(
                    concatMap((response) => {
                        const context: QueryPlaceStatusPayload = { request, requestPicks, state, trackBetContext, trackBetContextV2, slips };

                        this.eventBridgeService.notifySuccessfulBetPlacements(
                            {
                                betType: request.betSlips[0].betSlipType,
                                stake: request.betSlips[0].stake.amount,
                                totalPickCount: request.betSlips[0].bets.flatMap((bet) => bet.picks).length,
                                isEachWaySelected: undefined,
                                picks: request.betSlips[0].bets.flatMap((bet) => bet.picks),
                            },
                            request.betSlips[0].stake.currency,
                        );

                        return this.processResponse(response, context, balanceBeforePlacement);
                    }),
                    catchError((error: Error) => this.processError(error, request.requestId)),
                    startWith(
                        QuickBetActions.initBetPlacement(),
                        BetslipActions.setState({ state: BetslipState.Placing }),
                        BetslipPlaceBetActions.startedPlaceBet(),
                        BetslipTrackingActions.checkoutSlip({
                            requestId: action.payload.requestId,
                            slips: slips.map(mapSlipToTrackingSlip),
                        }),
                    ),
                );
            }),
        );
    });

    handleRequestPlaceBetEffect$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(QuickBetActions.requestPlaceBet),
            concatLatestFrom(() => this.store.select(betslipSelector)),
            exhaustMap(([action, state]) => {
                return from(this.betBuilderPlaceBetService.updateBetBuilderPicks(state, true)).pipe(
                    map(() => {
                        return QuickBetActions.placeBet(action);
                    }),
                    catchError((error: Error) => this.processError(error, action.payload.requestId)),
                );
            }),
        );
    });

    handleQueryStatusEffect$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(QuickBetActions.queryPlaceStatus),
            switchMap((action) => {
                return this.placeBetService.queryStatus(action.request.requestId).pipe(
                    concatMap((response) => {
                        const context: QueryPlaceStatusPayload = {
                            request: action.request,
                            requestPicks: action.requestPicks,
                            state: action.state,
                            trackBetContext: action.trackBetContext,
                            trackBetContextV2: action.trackBetContextV2,
                            slips: action.slips,
                        };

                        return this.processResponse(response, context, action.balanceAtPlacement);
                    }),
                    catchError((error: Error) => this.processError(error, action.request.requestId)),
                );
            }),
        );
    });

    private processError(error: Error | TechnicalError, requestId: string): Observable<Action> {
        const betPlacementErrors: BetslipError[] = [];

        if (error instanceof HttpErrorResponse && (error.status === 401 || error.status === 403)) {
            betPlacementErrors.push(new NotLoggedInError());
        } else {
            betPlacementErrors.push(new TechnicalError());
            this.logger.error(error, `Place bet failed with technical error for ${requestId}`);
        }

        const actions = [
            BetslipPlaceBetActions.placeBetFailed({ reason: PlaceBetFailedReason.TechnicalError }),
            BetslipErrorsActions.placeBetResponseErrors({
                pickErrors: emptyPickErrors(),
                betslipErrors: betPlacementErrors,
                slipErrors: {},
            }),
            BetslipActions.setState({ state: BetslipState.Edit }),
            QuickBetActions.setActionButton({ payload: QuickBetActionButtonState.Place }),
            QuickBetActions.finalizeBetPlacement(),
            QuickBetActions.requestRemoveMultiplePicks({ cleanBetslip: true }),
            QuickBetActions.showTechnicalErrorDialog(),
        ];

        if (this.betslipConfig.noWaitingBalanceRefresh) {
            this.balanceService.refresh();

            return from(actions);
        }

        return this.balanceService.refresh$().pipe(
            catchError((_) => {
                this.logger.error(`Balance refresh failed post bet placement for ${requestId}`);

                return of(null);
            }),
            switchMap((_) => actions),
        );
    }

    private processResponse(
        response: HttpResponse<PlaceBetResponse>,
        context: QueryPlaceStatusPayload,
        balanceBeforePlacement: number,
    ): Observable<Action> {
        if (response.status !== 200 || !response.body) {
            return this.processError(new TechnicalError(), context.request.requestId);
        }
        const content = response.body;
        const status = this.responseProcessor.getStatus(content);
        if (status === PlaceBetStatus.Pending) {
            const pendingResult = this.responseProcessor.processPending(content);

            return this.processPlaceBetPendingResponse(pendingResult, context, balanceBeforePlacement);
        } else {
            const result = this.responseProcessor.processDone(content, context);

            return this.processDonePlacement(result, context, status, balanceBeforePlacement);
        }
    }

    private processPlaceBetPendingResponse(
        pendingResult: { delay: number },
        context: QueryPlaceStatusPayload,
        balanceBeforePlacement: number,
    ): Observable<Action> {
        return of(
            QuickBetActions.queryPlaceStatus({
                ...context,
                balanceAtPlacement: balanceBeforePlacement,
            }),
        ).pipe(delay(pendingResult.delay));
    }

    private processDonePlacement(
        result: IPlacedBetslipResult,
        context: QueryPlaceStatusPayload,
        status: PlaceBetStatus,
        balanceBeforePlacement: number,
    ): Observable<Action> {
        const responseActions = new Observable<Action>((subscriber) => {
            subscriber.next(BetslipActions.setState({ state: BetslipState.Edit }));
            subscriber.next(
                QuickBetActions.finishPlaceBet({
                    request: context.request,
                    requestPicks: context.requestPicks,
                    placeBetResult: result,
                    state: context.state,
                }),
            );

            if (status === PlaceBetStatus.Success) {
                subscriber.next(RewardTokensActions.removeSelectedTokens());
                subscriber.next(RewardTokensActions.refreshPostPlacement());
            } else {
                subscriber.next(RewardTokensActions.refresh());
            }

            subscriber.complete();
        });

        if (result.status !== PlaceBetStatus.Success) {
            return responseActions;
        }

        const balanceShouldChange = result.slips.some((slip) => isPartialSlip(slip) || (isSucceededNonPartialSlip(slip) && !slip.isFreebet));

        if (this.betslipConfig.noWaitingBalanceRefresh) {
            this.balanceService.refresh();

            return responseActions.pipe(
                startWith(
                    QuickBetActions.trackBetPlacementResult({
                        request: context.request,
                        requestPicks: context.requestPicks,
                        placeBetResult: result,
                        state: context.state,
                    }),
                ),
            );
        }

        return this.balanceService.refresh$(balanceShouldChange, balanceBeforePlacement).pipe(
            //catch errors in case the refresh wasn't successful
            //we still need the show the user the placement response
            catchError((_) => {
                this.logger.error(`Balance refresh failed post bet placement for ${context.request.requestId}`);

                return of(null);
            }),
            switchMap((_) => responseActions),
            startWith(
                QuickBetActions.trackBetPlacementResult({
                    request: context.request,
                    requestPicks: context.requestPicks,
                    placeBetResult: result,
                    state: context.state,
                }),
            ),
        );
    }
}
